import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaSave,
  FaTimes,
  FaHistory,
  FaCity,
  FaDollarSign,
  FaClock,
  FaRoad,
  FaExchangeAlt,
  FaUsers,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const RateCard = () => {
  const [masterRateCard, setMasterRateCard] = useState({});
  const [cityRateCards, setCityRateCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMaster, setEditingMaster] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCityHistory, setSelectedCityHistory] = useState(null);
  const [history, setHistory] = useState([]);

  // Form states
  const [masterEditForm, setMasterEditForm] = useState({});
  const [cityEditForm, setCityEditForm] = useState({});
  const [newCityForm, setNewCityForm] = useState({
    city: "",
    perMinute: "",
    perHour: "",
    minChargeInCity: "",
    perKmInCity: "",
    minChargeOutstation: "",
    perKmOutstation: "",
  });
  const [bulkUpdateForm, setBulkUpdateForm] = useState({
    selectedCities: [],
    rates: {},
  });

  useEffect(() => {
    fetchAllRateCards();
  }, []);

  // Fetch all rate cards (master + cities)
  const fetchAllRateCards = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.get(
        API.ADMIN.RATE_CARDS_CITIES_ALL,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setMasterRateCard(response.data.data.masterRateCard || {});
        setCityRateCards(response.data.data.cities || []);
      }
    } catch (error) {
      console.error("Error fetching rate cards:", error);
      toast.error("Failed to fetch rate cards");
    } finally {
      setLoading(false);
    }
  };

  // Update master rate card
  const updateMasterRateCard = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.put(
        API.ADMIN.RATE_CARD_MASTER_UPDATE,
        masterEditForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMasterRateCard(response.data);
      setEditingMaster(false);
      setMasterEditForm({});
      toast.success("Master rate card updated successfully");
      fetchAllRateCards();
    } catch (error) {
      console.error("Error updating master rate card:", error);
      toast.error("Failed to update master rate card");
    }
  };

  // Create new city rate card
  const createCityRateCard = async () => {
    if (!newCityForm.city.trim()) {
      toast.error("City name is required");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.post(
        API.ADMIN.RATE_CARD_CITY_CREATE,
        newCityForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setShowAddCityModal(false);
        setNewCityForm({
          city: "",
          perMinute: "",
          perHour: "",
          minChargeInCity: "",
          perKmInCity: "",
          minChargeOutstation: "",
          perKmOutstation: "",
        });
        fetchAllRateCards();
      }
    } catch (error) {
      console.error("Error creating city rate card:", error);
      toast.error(
        error.response?.data?.message || "Failed to create city rate card"
      );
    }
  };

  // Update city rate card
  const updateCityRateCard = async (cityName) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.put(
        `${API.ADMIN.RATE_CARD_CITY_UPDATE}/${cityName}`,
        cityEditForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setEditingCity(null);
        setCityEditForm({});
        fetchAllRateCards();
      }
    } catch (error) {
      console.error("Error updating city rate card:", error);
      toast.error("Failed to update city rate card");
    }
  };

  // Delete city rate card
  const deleteCityRateCard = async (cityName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the rate card for ${cityName}?`
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.delete(
        `${API.ADMIN.RATE_CARD_CITY_DELETE}/${cityName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchAllRateCards();
      }
    } catch (error) {
      console.error("Error deleting city rate card:", error);
      toast.error("Failed to delete city rate card");
    }
  };

  // Copy master rate card to city
  const copyMasterToCity = async (cityName) => {
    if (!cityName.trim()) {
      toast.error("City name is required");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.post(
        API.ADMIN.RATE_CARD_COPY_MASTER,
        { city: cityName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchAllRateCards();
      }
    } catch (error) {
      console.error("Error copying master rate card:", error);
      toast.error(
        error.response?.data?.message || "Failed to copy master rate card"
      );
    }
  };

  // Bulk update cities
  const bulkUpdateCities = async () => {
    if (bulkUpdateForm.selectedCities.length === 0) {
      toast.error("Please select at least one city");
      return;
    }

    if (Object.keys(bulkUpdateForm.rates).length === 0) {
      toast.error("Please provide at least one rate to update");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.post(
        API.ADMIN.RATE_CARD_BULK_UPDATE,
        {
          cities: bulkUpdateForm.selectedCities,
          rates: bulkUpdateForm.rates,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setShowBulkUpdateModal(false);
        setBulkUpdateForm({ selectedCities: [], rates: {} });
        fetchAllRateCards();
      }
    } catch (error) {
      console.error("Error bulk updating cities:", error);
      toast.error("Failed to bulk update cities");
    }
  };

  // Fetch city rate card history
  const fetchCityHistory = async (cityName) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.get(
        `${API.ADMIN.RATE_CARD_HISTORY}/${cityName}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log(`${cityName} history response:`, response.data); // Debug log

      if (response.data.success) {
        setHistory(response.data.data?.history || []);
        setSelectedCityHistory(cityName);
        setShowHistoryModal(true);
      } else {
        setHistory([]);
        setSelectedCityHistory(cityName);
        setShowHistoryModal(true);
        console.log("API returned success: false");
      }
    } catch (error) {
      console.error("Error fetching city history:", error);
      console.error("Response data:", error.response?.data);

      // Still show modal with empty state
      setHistory([]);
      setSelectedCityHistory(cityName);
      setShowHistoryModal(true);

      toast.error("Failed to fetch city history");
    }
  };

  // Fetch master rate card history
  const fetchMasterHistory = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      // Use correct endpoint: /api/admin/ratecards/master/history
      const masterHistoryUrl = `${API.ADMIN.RATE_CARD_MASTER_UPDATE}/history`;
      console.log("Fetching master history from:", masterHistoryUrl);

      const response = await axiosInstance.get(masterHistoryUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Master history response:", response.data);

      if (response.data.success) {
        const historyData =
          response.data.data?.history || response.data.history || [];
        console.log("Master history data:", historyData);
        setHistory(historyData);
        setSelectedCityHistory("Master Rate Card");
        setShowHistoryModal(true);
      } else {
        console.log("API returned success: false, showing empty state");
        setHistory([]);
        setSelectedCityHistory("Master Rate Card");
        setShowHistoryModal(true);
      }
    } catch (error) {
      console.error("Error fetching master history:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);

      // Still show modal with empty state for better UX
      setHistory([]);
      setSelectedCityHistory("Master Rate Card");
      setShowHistoryModal(true);

      toast.error("Failed to fetch master rate card history");
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return "Date not available";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleString();
    } catch (error) {
      return "Invalid date";
    }
  };

  // Start editing city
  const startEditingCity = (city) => {
    setEditingCity(city.city);
    setCityEditForm({
      perMinute: city.perMinute || "",
      perHour: city.perHour || "",
      minChargeInCity: city.minChargeInCity || "",
      perKmInCity: city.perKmInCity || "",
      minChargeOutstation: city.minChargeOutstation || "",
      perKmOutstation: city.perKmOutstation || "",
    });
  };

  // Start editing master
  const startEditingMaster = () => {
    setEditingMaster(true);
    setMasterEditForm({
      perMinute: masterRateCard.perMinute || "",
      perHour: masterRateCard.perHour || "",
      minChargeInCity: masterRateCard.minChargeInCity || "",
      perKmInCity: masterRateCard.perKmInCity || "",
      minChargeOutstation: masterRateCard.minChargeOutstation || "",
      perKmOutstation: masterRateCard.perKmOutstation || "",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading rate cards...</span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Rate Card Management
            </h1>
            <p className="text-gray-600">
              Manage master rate card and city-specific rate configurations
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddCityModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FaPlus className="mr-2" />
              Add City
            </button>
            <button
              onClick={() => setShowBulkUpdateModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FaUsers className="mr-2" />
              Bulk Update
            </button>
          </div>
        </div>
      </div>

      {/* Master Rate Card */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaDollarSign className="text-blue-500 text-2xl mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Master Rate Card
                </h2>
                <p className="text-gray-600">Default rates for all cities</p>
              </div>
            </div>
            {!editingMaster && (
              <div className="flex gap-2">
                <button
                  onClick={() => fetchMasterHistory()}
                  className="flex items-center px-3 py-1 text-gray-600 hover:bg-gray-50 rounded"
                >
                  <FaHistory className="mr-1" />
                  History
                </button>
                <button
                  onClick={startEditingMaster}
                  className="flex items-center px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <FaEdit className="mr-1" />
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {editingMaster ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(masterEditForm).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={(e) =>
                      setMasterEditForm({
                        ...masterEditForm,
                        [key]: e.target.value,
                      })
                    }
                    className="border rounded-lg px-3 py-2 w-full"
                  />
                </div>
              ))}
              <div className="col-span-full flex gap-2 mt-4">
                <button
                  onClick={updateMasterRateCard}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <FaSave className="mr-2" />
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditingMaster(false);
                    setMasterEditForm({});
                  }}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <FaTimes className="mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(masterRateCard)
                .filter(
                  ([key]) =>
                    key !== "_id" &&
                    key !== "updatedAt" &&
                    key !== "createdAt" &&
                    key !== "__v"
                )
                .map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {key.includes("per") ||
                      key.includes("Charge") ||
                      key.includes("Km")
                        ? formatCurrency(value)
                        : value}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* City Rate Cards */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FaCity className="text-green-500 text-2xl mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                City-Specific Rate Cards
              </h2>
              <p className="text-gray-600">
                {cityRateCards.length} cities configured
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {cityRateCards.length > 0 ? (
            <div className="space-y-6">
              {cityRateCards.map((city) => (
                <div key={city.city} className="border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <FaCity className="text-blue-500 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-800">
                        {city.city}
                      </h3>
                      <span className="ml-2 text-sm text-gray-500">
                        Updated:{" "}
                        {city.updatedAt
                          ? new Date(city.updatedAt).toLocaleDateString()
                          : "Unknown"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchCityHistory(city.city)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        title="View History"
                      >
                        <FaHistory />
                      </button>
                      {editingCity !== city.city && (
                        <button
                          onClick={() => startEditingCity(city)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      )}
                      <button
                        onClick={() => copyMasterToCity(city.city)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                        title="Copy from Master"
                      >
                        <FaCopy />
                      </button>
                      <button
                        onClick={() => deleteCityRateCard(city.city)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  {editingCity === city.city ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(cityEditForm).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={value}
                            onChange={(e) =>
                              setCityEditForm({
                                ...cityEditForm,
                                [key]: e.target.value,
                              })
                            }
                            className="border rounded-lg px-3 py-2 w-full"
                          />
                        </div>
                      ))}
                      <div className="col-span-full flex gap-2 mt-4">
                        <button
                          onClick={() => updateCityRateCard(city.city)}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <FaSave className="mr-2" />
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setEditingCity(null);
                            setCityEditForm({});
                          }}
                          className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          <FaTimes className="mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(city)
                        .filter(
                          ([key]) =>
                            key !== "_id" &&
                            key !== "city" &&
                            key !== "updatedAt" &&
                            key !== "createdAt"
                        )
                        .map(([key, value]) => (
                          <div key={key} className="bg-gray-50 rounded p-3">
                            <div className="text-sm font-medium text-gray-500 mb-1">
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </div>
                            <div className="text-lg font-bold text-gray-900">
                              {key.includes("per") ||
                              key.includes("Charge") ||
                              key.includes("Km")
                                ? formatCurrency(value)
                                : value}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaCity className="text-gray-400 text-4xl mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                No city-specific rate cards found
              </p>
              <button
                onClick={() => setShowAddCityModal(true)}
                className="flex items-center mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FaPlus className="mr-2" />
                Add First City
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add City Modal */}
      {showAddCityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New City Rate Card</h3>
              <button
                onClick={() => setShowAddCityModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City Name *
                </label>
                <input
                  type="text"
                  value={newCityForm.city}
                  onChange={(e) =>
                    setNewCityForm({ ...newCityForm, city: e.target.value })
                  }
                  className="border rounded-lg px-3 py-2 w-full"
                  placeholder="Enter city name"
                />
              </div>

              {Object.entries(newCityForm)
                .filter(([key]) => key !== "city")
                .map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={value}
                      onChange={(e) =>
                        setNewCityForm({
                          ...newCityForm,
                          [key]: e.target.value,
                        })
                      }
                      className="border rounded-lg px-3 py-2 w-full"
                    />
                  </div>
                ))}
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={createCityRateCard}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FaSave className="mr-2" />
                Create Rate Card
              </button>
              <button
                onClick={() => {
                  const cityName = prompt(
                    "Enter city name to copy master rates:"
                  );
                  if (cityName) copyMasterToCity(cityName);
                }}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FaCopy className="mr-2" />
                Copy from Master
              </button>
              <button
                onClick={() => setShowAddCityModal(false)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <FaTimes className="mr-2" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Bulk Update Cities</h3>
              <button
                onClick={() => setShowBulkUpdateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Cities to Update
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-3">
                {cityRateCards.map((city) => (
                  <label key={city.city} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkUpdateForm.selectedCities.includes(
                        city.city
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBulkUpdateForm({
                            ...bulkUpdateForm,
                            selectedCities: [
                              ...bulkUpdateForm.selectedCities,
                              city.city,
                            ],
                          });
                        } else {
                          setBulkUpdateForm({
                            ...bulkUpdateForm,
                            selectedCities:
                              bulkUpdateForm.selectedCities.filter(
                                (c) => c !== city.city
                              ),
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    {city.city}
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rates to Update (leave empty to skip)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "perMinute",
                  "perHour",
                  "minChargeInCity",
                  "perKmInCity",
                  "minChargeOutstation",
                  "perKmOutstation",
                ].map((key) => (
                  <div key={key}>
                    <label className="block text-sm text-gray-600 mb-1">
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkUpdateForm.rates[key] || ""}
                      onChange={(e) =>
                        setBulkUpdateForm({
                          ...bulkUpdateForm,
                          rates: {
                            ...bulkUpdateForm.rates,
                            [key]: e.target.value,
                          },
                        })
                      }
                      className="border rounded-lg px-3 py-2 w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={bulkUpdateCities}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FaSave className="mr-2" />
                Update Selected Cities
              </button>
              <button
                onClick={() => setShowBulkUpdateModal(false)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <FaTimes className="mr-2" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Rate Card History - {selectedCityHistory}
              </h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              {history && history.length > 0 ? (
                history.map((entry) => (
                  <div key={entry._id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">
                        {entry.updatedBy || "Unknown User"}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(entry.createdAt)}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <pre className="text-sm">
                        {JSON.stringify(entry.updates, null, 2)}
                      </pre>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <FaHistory className="text-gray-400 text-4xl mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No history available</p>
                  <p className="text-sm text-gray-400">
                    {selectedCityHistory === "Master Rate Card"
                      ? "No changes have been made to the master rate card yet."
                      : `No changes have been made to ${selectedCityHistory} rate card yet.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateCard;
